import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, Mail, Image, Save, Eye, EyeOff, CheckCircle, AlertCircle, Clock, Globe, MapPin, Database, CalendarDays } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common US timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5/4' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: 'UTC-6/5' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: 'UTC-7/6' },
  { value: 'America/Phoenix', label: 'Arizona (MST)', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: 'UTC-8/7' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', offset: 'UTC-9/8' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', offset: 'UTC-10' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (AST)', offset: 'UTC-4' },
];

export default function Settings() {
  const { user, clinic, refreshClinic } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [locations, setLocations] = useState([]);
  
  // Clinic info form
  const [clinicForm, setClinicForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    timezone: 'America/Chicago',
    default_location_id: '',
    google_places_api_key: ''
  });
  
  // Email settings form
  const [emailForm, setEmailForm] = useState({
    email_sender: '',
    email_password: ''
  });
  
  // Logo settings
  const [logoUrl, setLogoUrl] = useState('');
  const [hasEmailConfigured, setHasEmailConfigured] = useState(false);
  
  // Practice Fusion settings
  const [pfSettings, setPfSettings] = useState({
    practice_fusion_enabled: false,
    practice_fusion_token_url: '',
    has_practice_fusion_configured: false
  });
  const [showTokenUrl, setShowTokenUrl] = useState(false);

  // Pay period settings (bi-weekly; only the anchor/start date is configurable)
  const [payPeriodAnchor, setPayPeriodAnchor] = useState('');
  const [savingPayPeriod, setSavingPayPeriod] = useState(false);

  // Platform settings (super admin only)
  const isSuperAdmin = user?.role === 'super_admin';
  const [platformForm, setPlatformForm] = useState({ intake_forward_base_url: '' });
  const [savingPlatform, setSavingPlatform] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      axios.get(`${API_URL}/api/platform-settings`)
        .then(res => setPlatformForm({ intake_forward_base_url: res.data?.intake_forward_base_url || '' }))
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  const savePlatformSettings = async () => {
    try {
      setSavingPlatform(true);
      const res = await axios.put(`${API_URL}/api/platform-settings`, { intake_forward_base_url: platformForm.intake_forward_base_url });
      setPlatformForm({ intake_forward_base_url: res.data?.intake_forward_base_url || '' });
      toast.success('Platform settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save platform settings');
    } finally {
      setSavingPlatform(false);
    }
  };

  useEffect(() => {
    fetchClinicData();
  }, [user?.clinic_id]);

  const fetchClinicData = async () => {
    if (!user?.clinic_id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // Fetch clinic data and locations in parallel
      const [clinicResponse, locationsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/clinics/${user.clinic_id}`),
        axios.get(`${API_URL}/api/locations`)
      ]);
      
      const data = clinicResponse.data;
      const locData = locationsResponse.data.items || locationsResponse.data;
      setLocations(Array.isArray(locData) ? locData : []);
      
      setClinicForm({
        name: data.name || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
        timezone: data.timezone || 'America/Chicago',
        default_location_id: data.default_location_id || '',
        google_places_api_key: data.google_places_api_key || ''
      });
      
      setEmailForm({
        email_sender: data.email_sender || '',
        email_password: '' // Don't show password
      });
      
      setLogoUrl(data.logo_url || '');
      setHasEmailConfigured(data.has_email_configured || false);
      setPayPeriodAnchor(data.pay_period_anchor_date || '');
      
      // Load Practice Fusion settings
      setPfSettings({
        practice_fusion_enabled: data.practice_fusion_enabled || false,
        practice_fusion_token_url: data.practice_fusion_token_url || '',
        has_practice_fusion_configured: data.has_practice_fusion_configured || false
      });
    } catch (error) {
      toast.error('Failed to load clinic settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClinicInfo = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/clinics/${user.clinic_id}/settings`, clinicForm);
      toast.success('Clinic information updated');
      if (refreshClinic) await refreshClinic();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clinic info');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayPeriod = async () => {
    try {
      setSavingPayPeriod(true);
      await axios.put(`${API_URL}/api/clinics/${user.clinic_id}/settings`, { pay_period_anchor_date: payPeriodAnchor || '' });
      toast.success('Pay period updated');
      if (refreshClinic) await refreshClinic();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pay period');
    } finally {
      setSavingPayPeriod(false);
    }
  };

  // Compute the current bi-weekly period from an anchor date (defaults to Jan 12, 2026)
  const getPeriodPreview = (anchorStr) => {
    const anchor = anchorStr ? new Date(anchorStr + 'T00:00:00') : new Date('2026-01-12T00:00:00');
    if (isNaN(anchor.getTime())) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const msPerDay = 86400000;
    const daysSince = Math.floor((today - anchor) / msPerDay);
    const periodNum = Math.floor(daysSince / 14);
    const start = new Date(anchor.getTime() + periodNum * 14 * msPerDay);
    const end = new Date(start.getTime() + 13 * msPerDay);
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleSaveEmailSettings = async () => {
    try {
      setSaving(true);
      const payload = { email_sender: emailForm.email_sender };
      // Only include password if it was changed (not empty)
      if (emailForm.email_password) {
        payload.email_password = emailForm.email_password;
      }
      
      await axios.put(`${API_URL}/api/clinics/${user.clinic_id}/settings`, payload);
      toast.success('Email settings updated');
      setHasEmailConfigured(true);
      setEmailForm(prev => ({ ...prev, email_password: '' })); // Clear password field
      if (refreshClinic) await refreshClinic();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogo = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/clinics/${user.clinic_id}/settings`, { logo_url: logoUrl });
      toast.success('Logo updated');
      if (refreshClinic) await refreshClinic();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update logo');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePracticeFusionSettings = async () => {
    try {
      setSaving(true);
      const payload = {
        practice_fusion_enabled: pfSettings.practice_fusion_enabled
      };
      
      // Only include token URL if user entered a new one
      if (pfSettings.practice_fusion_token_url) {
        payload.practice_fusion_token_url = pfSettings.practice_fusion_token_url;
      }
      
      await axios.put(`${API_URL}/api/clinics/${user.clinic_id}/settings`, payload);
      
      toast.success('Practice Fusion settings updated');
      if (refreshClinic) await refreshClinic();
      
      // Refresh clinic data and reset the input
      await fetchClinicData();
      setPfSettings(prev => ({ ...prev, practice_fusion_token_url: '' }));
      setShowTokenUrl(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Practice Fusion settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user?.clinic_id) {
    if (isSuperAdmin) {
      return (
        <div className="space-y-6 animate-fade-in" data-testid="platform-settings-page">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Platform Settings</h1>
            <p className="text-slate-500 text-sm mt-1">Global configuration managed by the platform owner</p>
          </div>
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="w-5 h-5 text-[#0073BB]" />
                Patient Intake Forwarding
              </CardTitle>
              <CardDescription>
                When set, every submitted patient intake form is also POSTed to <span className="font-mono text-slate-700">{'{base_url}'}/api/intake</span>. Leave blank to disable forwarding.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="intake-base-url">External Base URL</Label>
                <Input
                  id="intake-base-url"
                  placeholder="http://3.135.104.192:8080"
                  value={platformForm.intake_forward_base_url}
                  onChange={(e) => setPlatformForm({ intake_forward_base_url: e.target.value })}
                  className="mt-1 font-mono"
                  data-testid="intake-base-url-input"
                />
                {platformForm.intake_forward_base_url?.trim() && (
                  <p className="mt-2 text-xs text-slate-500">
                    Forwarding to: <span className="font-mono text-[#0073BB]">{platformForm.intake_forward_base_url.trim().replace(/\/$/, '')}/api/intake</span>
                  </p>
                )}
              </div>
              <Button onClick={savePlatformSettings} disabled={savingPlatform} className="bg-[#0073BB] hover:bg-[#005A93]" data-testid="save-platform-settings-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingPlatform ? 'Saving...' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <Card>
          <CardContent className="py-16 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <p className="text-slate-500">No clinic assigned to your account.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Clinic Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Configure your clinic information and email settings</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-2xl bg-blue-50 border border-blue-100">
          <TabsTrigger value="general" className="data-[state=active]:bg-white data-[state=active]:text-blue-700" data-testid="tab-general">
            <Building2 className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-white data-[state=active]:text-blue-700" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-white data-[state=active]:text-blue-700" data-testid="tab-branding">
            <Image className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-white data-[state=active]:text-blue-700" data-testid="tab-integrations">
            <Database className="w-4 h-4 mr-2" />
            EHR
          </TabsTrigger>
          <TabsTrigger value="payperiod" className="data-[state=active]:bg-white data-[state=active]:text-blue-700" data-testid="tab-payperiod">
            <CalendarDays className="w-4 h-4 mr-2" />
            Pay Period
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Clinic Information</CardTitle>
              <CardDescription>Update your clinic's basic information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Clinic Name</Label>
                  <Input
                    id="name"
                    value={clinicForm.name}
                    onChange={(e) => setClinicForm({ ...clinicForm, name: e.target.value })}
                    placeholder="Your Clinic Name"
                    className="mt-1.5"
                    data-testid="clinic-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={clinicForm.phone}
                    onChange={(e) => setClinicForm({ ...clinicForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1.5"
                    data-testid="clinic-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={clinicForm.address}
                  onChange={(e) => setClinicForm({ ...clinicForm, address: e.target.value })}
                  placeholder="123 Medical Dr, City, State ZIP"
                  className="mt-1.5"
                  data-testid="clinic-address-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Contact Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={clinicForm.email}
                  onChange={(e) => setClinicForm({ ...clinicForm, email: e.target.value })}
                  placeholder="contact@yourclinic.com"
                  className="mt-1.5"
                  data-testid="clinic-email-input"
                />
              </div>
              
              {/* Timezone Selector */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-teal-600" />
                  <h3 className="font-medium text-slate-900">Timezone Settings</h3>
                </div>
                <div>
                  <Label htmlFor="timezone">Clinic Timezone</Label>
                  <Select 
                    value={clinicForm.timezone} 
                    onValueChange={(value) => setClinicForm({ ...clinicForm, timezone: value })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="timezone-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span>{tz.label}</span>
                            <span className="text-xs text-slate-400">({tz.offset})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    This timezone will be used for all reports, daily closing, and dashboard statistics
                  </p>
                </div>
              </div>
              
              {/* Default Location Selector */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-slate-900">Default Location</h3>
                </div>
                <div>
                  <Label htmlFor="default_location">Default Location</Label>
                  <Select 
                    value={clinicForm.default_location_id || "none"} 
                    onValueChange={(value) => setClinicForm({ ...clinicForm, default_location_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="default-location-select">
                      <SelectValue placeholder="Select default location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No default location</SelectItem>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span>{loc.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    The default location will be pre-selected when creating new visits and inventory items
                  </p>
                  {locations.length === 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      No locations found. Please add locations first in the Locations page.
                    </p>
                  )}
                </div>
              </div>
              <div className="pt-2">
                <Label htmlFor="google_places_api_key">Google Places API Key</Label>
                <Input
                  id="google_places_api_key"
                  type="text"
                  value={clinicForm.google_places_api_key}
                  onChange={(e) => setClinicForm({ ...clinicForm, google_places_api_key: e.target.value })}
                  placeholder="AIza..."
                  className="mt-1 font-mono"
                  data-testid="google-places-api-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enables address autocomplete on the Patient Intake form (pharmacy &amp; patient address). Enable "Maps JavaScript API" + "Places API" and restrict the key to your domain in Google Cloud Console.
                </p>
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveClinicInfo} disabled={saving} data-testid="save-clinic-info-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Email Configuration
                {hasEmailConfigured ? (
                  <span className="flex items-center text-sm font-normal text-green-600">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center text-sm font-normal text-amber-600">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Not Configured
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Configure your clinic's email settings to send payment receipts to patients.
                Use a Gmail account with an App Password for secure email delivery.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <p className="font-medium mb-2">How to get a Gmail App Password:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Go to your Google Account → Security</li>
                  <li>Enable 2-Step Verification if not already enabled</li>
                  <li>Search for "App passwords" in Google Account settings</li>
                  <li>Generate a new app password for "Mail"</li>
                  <li>Copy the 16-character password and paste it below</li>
                </ol>
              </div>
              
              <div>
                <Label htmlFor="email_sender">Gmail Address</Label>
                <Input
                  id="email_sender"
                  type="email"
                  value={emailForm.email_sender}
                  onChange={(e) => setEmailForm({ ...emailForm, email_sender: e.target.value })}
                  placeholder="yourclinic@gmail.com"
                  className="mt-1.5"
                  data-testid="email-sender-input"
                />
                <p className="text-xs text-slate-500 mt-1">This will be the "From" address for receipt emails</p>
              </div>
              
              <div>
                <Label htmlFor="email_password">Gmail App Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="email_password"
                    type={showPassword ? 'text' : 'password'}
                    value={emailForm.email_password}
                    onChange={(e) => setEmailForm({ ...emailForm, email_password: e.target.value })}
                    placeholder={hasEmailConfigured ? '••••••••••••••••' : 'Enter 16-character app password'}
                    className="pr-10"
                    data-testid="email-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {hasEmailConfigured 
                    ? 'Leave empty to keep the current password, or enter a new one to update'
                    : 'Enter the 16-character app password from Google'}
                </p>
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveEmailSettings} disabled={saving || !emailForm.email_sender} data-testid="save-email-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Email Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Clinic Logo</CardTitle>
              <CardDescription>
                Add your clinic logo to appear on payment receipt emails sent to patients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="logo_url">Logo URL</Label>
                <Input
                  id="logo_url"
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/your-logo.png"
                  className="mt-1.5"
                  data-testid="logo-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter a direct URL to your logo image (PNG, JPG, or SVG). Recommended size: 200x60 pixels.
                </p>
              </div>
              
              {logoUrl && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <Label className="text-sm text-slate-600 mb-2 block">Preview</Label>
                  <div className="bg-white p-4 rounded border flex items-center justify-center min-h-[80px]">
                    <img 
                      src={logoUrl} 
                      alt="Clinic logo preview" 
                      className="max-h-16 max-w-[200px] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <p className="text-slate-400 text-sm hidden">Failed to load image</p>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveLogo} disabled={saving} data-testid="save-logo-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Logo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EHR Integrations Tab - Practice Fusion */}
        <TabsContent value="integrations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Practice Fusion Integration
                {pfSettings.practice_fusion_enabled ? (
                  <span className="flex items-center text-sm font-normal text-green-600">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Enabled
                  </span>
                ) : (
                  <span className="flex items-center text-sm font-normal text-slate-400">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Disabled
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Connect to Practice Fusion EHR to search and import patients directly into your clinic records.
                You can import patients from the Patients page once configured.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                <div>
                  <Label className="text-base font-medium">Enable Practice Fusion Integration</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    When enabled, you can search and import patients from Practice Fusion
                  </p>
                </div>
                <Switch
                  checked={pfSettings.practice_fusion_enabled}
                  onCheckedChange={(checked) => setPfSettings(prev => ({ ...prev, practice_fusion_enabled: checked }))}
                  data-testid="pf-enabled-switch"
                />
              </div>

              {/* Token URL Configuration */}
              <div>
                <Label htmlFor="pf_token_url">Authorization Token URL</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="pf_token_url"
                    type={pfSettings.practice_fusion_token_url ? (showTokenUrl ? "url" : "password") : "url"}
                    value={pfSettings.practice_fusion_token_url}
                    onChange={(e) => setPfSettings(prev => ({ ...prev, practice_fusion_token_url: e.target.value }))}
                    placeholder={pfSettings.has_practice_fusion_configured ? "••••••••••••••••" : "https://your-server.com/api/pf-token"}
                    className="font-mono"
                    data-testid="pf-token-url-input"
                  />
                  {pfSettings.practice_fusion_token_url && (
                    <button
                      type="button"
                      onClick={() => setShowTokenUrl(!showTokenUrl)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      {showTokenUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {pfSettings.has_practice_fusion_configured 
                    ? "URL is configured. Enter a new URL to replace it, or leave blank to keep the current one."
                    : "Enter the URL that returns the Practice Fusion authorization token."
                  }
                </p>
              </div>

              {/* Usage Instructions */}
              {pfSettings.practice_fusion_enabled && pfSettings.has_practice_fusion_configured && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800 mb-2">Integration Ready!</p>
                      <p className="text-blue-700">
                        You can now search and import patients from Practice Fusion directly from the <strong>Patients</strong> page.
                        The token will be fetched automatically from the configured URL.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Not configured warning */}
              {pfSettings.practice_fusion_enabled && !pfSettings.has_practice_fusion_configured && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 mb-1">Token URL Required</p>
                      <p className="text-amber-700">
                        Please enter the Authorization Token URL above to complete the setup.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button onClick={handleSavePracticeFusionSettings} disabled={saving} data-testid="save-pf-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save EHR Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pay Period Settings Tab */}
        <TabsContent value="payperiod" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pay Period</CardTitle>
              <CardDescription>
                Set the date a pay period begins. Periods are bi-weekly (14 days) and all bonus,
                closing and payslip reports are grouped using this anchor.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="max-w-sm">
                <Label htmlFor="pay-period-anchor">Pay Period Start Date (anchor)</Label>
                <Input
                  id="pay-period-anchor"
                  type="date"
                  value={payPeriodAnchor}
                  onChange={(e) => setPayPeriodAnchor(e.target.value)}
                  data-testid="pay-period-anchor-input"
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Pick any date on which one of your pay periods started. Leave blank to use the
                  default (Jan 12, 2026).
                </p>
              </div>

              <div className="rounded-md border border-blue-100 bg-blue-50/60 p-4" data-testid="pay-period-preview">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-700">
                  <CalendarDays className="h-4 w-4" /> Current period preview
                </div>
                <p className="mt-1 font-mono text-sm font-semibold text-slate-800">
                  {getPeriodPreview(payPeriodAnchor) || 'Enter a valid date'}
                </p>
                <p className="mt-1 text-xs text-slate-500">Length: 14 days (bi-weekly)</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSavePayPeriod} disabled={savingPayPeriod} data-testid="save-pay-period-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingPayPeriod ? 'Saving...' : 'Save Pay Period'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
